import React, { useRef, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { API, axios } from '../utils/api';
import { useAuth, paginaDelRuolo } from '../context/AuthContext';
import { ScenaAccesso, useScenaAccesso } from '../components/accesso/ScenaAccesso';
import {
  messaggioErrore, useInvioUnico, TestaCard, Avviso, Bottone, Campo, CampoPassword, IndicatoreRobustezza,
} from '../components/accesso/campi';
import { LUNGHEZZE, campoDellErrore, erroriCollaboratore } from '../utils/registrazione';

// Campo del modulo → nome con cui è segnato l'errore (erroriCollaboratore e TESTI_BACKEND dicono 'invito')
const CAMPO_ERRORE = { codiceInvito: 'invito' };

// Registrazione dei collaboratori con il codice invito, nello stile del login (un passo solo).
// Stessa rotta di prima (POST /api/auth/register-collaboratore), poi login, "Benvenuti" e dashboard.

const TESTI_BACKEND = [
  [/email/i, 'Esiste già un account con questa email: accedete, oppure usate "Password dimenticata?".', 'email'],
  [/invito/i, 'Codice invito non valido: controllatelo (maiuscole e minuscole contano).', 'invito'],
];

const RegisterCollaboratore = () => {
  const { login, isAuthenticated, userType, loading } = useAuth();
  const scena = useScenaAccesso('MK Client — Registrazione collaboratori');
  const { scuoti, entra } = scena;
  const entrando = useRef(false);

  const [dati, setDati] = useState({ nome: '', cognome: '', email: '', codiceInvito: '', password: '', conferma: '' });
  const [vedi, setVedi] = useState(false);
  const [avviso, setAvviso] = useState(null);
  const [campoErrato, setCampoErrato] = useState(null);
  const [attesa, setAttesa] = useState(false);
  const campi = useRef({});
  const invio = useInvioUnico();

  const rif = (nome) => (el) => { campi.current[nome] = el; };

  // Correggendo un campo segnato in rosso il bordo torna normale (il codice invito è segnato come 'invito')
  const cambia = (campo) => (valore) => {
    setDati((d) => ({ ...d, [campo]: valore }));
    if (campoErrato === (CAMPO_ERRORE[campo] || campo)) setCampoErrato(null);
  };

  const errore = ({ campo, testo }) => {
    setAvviso({ testo });
    setCampoErrato(campo);
    scuoti();
    campi.current[campo]?.focus();
  };

  const crea = async (e) => {
    e.preventDefault();
    const sbagliato = erroriCollaboratore(dati);
    if (sbagliato) { errore(sbagliato); return; }
    if (!invio.blocca()) return; // doppio Invio: una richiesta sola

    setAvviso(null);
    setAttesa(true);
    try {
      await axios.post(`${API}/auth/register-collaboratore`, {
        nome: dati.nome.trim(),
        cognome: dati.cognome.trim(),
        email: dati.email.trim(),
        password: dati.password,
        codice_invito: dati.codiceInvito.trim(),
      });
    } catch (err) {
      invio.sblocca();
      setAttesa(false);
      const dettaglio = typeof err.response?.data?.detail === 'string' ? err.response.data.detail : '';
      if (err.response?.status === 422) { errore({ campo: 'email', testo: "Controllate l'email: sembra incompleta." }); return; }
      const noto = TESTI_BACKEND.find(([re]) => re.test(dettaglio));
      errore(noto ? { campo: noto[2], testo: noto[1] } : { campo: campoDellErrore(dettaglio), testo: messaggioErrore(err, 'Registrazione non riuscita. Riprovate') });
      return;
    }

    entrando.current = true;
    const esito = await login(dati.email.trim(), dati.password, true);
    if (!esito.success) {
      entrando.current = false;
      setAttesa(false);
      setAvviso({ testo: 'Account creato. Ora accedete dalla pagina di accesso.', ok: true });
      return;
    }
    entra(esito, { parola: 'Benvenuti', etichetta: 'Account creato' });
  };

  // Già entrati: alla propria area, non al modulo
  if (!loading && isAuthenticated && !entrando.current) return <Navigate to={paginaDelRuolo(userType)} replace />;

  return (
    <ScenaAccesso scena={scena}>
      <TestaCard etichetta={scena.saluto} titolo="Entrate nella" corsivo="squadra"
        sotto="Registrazione dei collaboratori, con il codice invito ricevuto da MK Consulting." />

      <div className="body">
        <Avviso avviso={avviso} />
        <form onSubmit={crea} noValidate data-testid="registrazione-collaboratore">
          <div className="coppia">
            <Campo id="col-nome" etichetta="Nome" autoComplete="given-name" autoCapitalize="words" ref={rif('nome')} maxLength={LUNGHEZZE.nome}
              valore={dati.nome} onChange={cambia('nome')} invalido={campoErrato === 'nome'} data-testid="collab-nome" />
            <Campo id="col-cognome" etichetta="Cognome" autoComplete="family-name" autoCapitalize="words" ref={rif('cognome')} maxLength={LUNGHEZZE.cognome}
              valore={dati.cognome} onChange={cambia('cognome')} invalido={campoErrato === 'cognome'} data-testid="collab-cognome" />
          </div>
          <Campo id="col-email" etichetta="Email" type="email" inputMode="email" autoComplete="email" ref={rif('email')} maxLength={LUNGHEZZE.email}
            valore={dati.email} onChange={cambia('email')} invalido={campoErrato === 'email'} data-testid="collab-email" />
          <Campo id="col-invito" etichetta="Codice invito" autoComplete="off" autoCapitalize="off" spellCheck={false} ref={rif('invito')}
            valore={dati.codiceInvito} onChange={cambia('codiceInvito')} invalido={campoErrato === 'invito'} data-testid="collab-invito" />
          <CampoPassword id="col-password" autoComplete="new-password" ref={rif('password')}
            valore={dati.password} onChange={cambia('password')} invalido={campoErrato === 'password'}
            visibile={vedi} onVisibile={() => setVedi((v) => !v)} data-testid="collab-password" />
          <IndicatoreRobustezza password={dati.password} />
          <CampoPassword id="col-conferma" etichetta="Ripetete la password" autoComplete="new-password" ref={rif('conferma')}
            valore={dati.conferma} onChange={cambia('conferma')} invalido={campoErrato === 'conferma'}
            visibile={vedi} data-testid="collab-conferma" />
          <Bottone attesa={attesa} data-testid="collab-crea">Crea l'account</Bottone>
        </form>

        <div className="links">
          Avete già un account? <Link to="/login">Accedete</Link>
        </div>
      </div>
    </ScenaAccesso>
  );
};

export default RegisterCollaboratore;
